// ABOUTME: utility functions for caching tenant branding data in localStorage
// ABOUTME: enables themed login pages before authentication by storing branding data locally

interface TenantBranding {
  logo?: string;
  backgroundImage?: string;
  primaryColor?: string;
}

interface CachedTenantData {
  name?: string;
  branding: TenantBranding;
}

const STORAGE_KEY = "tenantBranding";

export const cacheTenantBranding = (
  tenantSlug: string,
  branding: TenantBranding,
  tenantName?: string,
): void => {
  try {
    const cached = getCachedBrandings();
    cached[tenantSlug] = {
      branding,
      name: tenantName,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));

    // dispatch custom event to trigger theme update
    window.dispatchEvent(new Event("tenantBrandingUpdated"));
  } catch (_error) {}
};

export const getCachedTenantBranding = (
  tenantSlug: string,
): TenantBranding | null => {
  try {
    const cached = getCachedBrandings();
    const cachedData = cached[tenantSlug];

    if (!cachedData) return null;

    // return cached branding data
    return cachedData.branding;
  } catch (_error) {
    return null;
  }
};

export const getCachedTenantData = (
  tenantSlug: string,
): CachedTenantData | null => {
  try {
    const cached = getCachedBrandings();
    const cachedData = cached[tenantSlug];

    if (!cachedData) return null;

    return {
      name: cachedData.name,
      branding: cachedData.branding,
    };
  } catch (_error) {
    return null;
  }
};

export const getMostRecentTenantData = ():
  | (CachedTenantData & { slug: string })
  | null => {
  try {
    const cached = getCachedBrandings();
    const entries = Object.entries(cached);

    if (entries.length === 0) return null;

    // find the most recently updated tenant
    let mostRecent = entries[0];
    let mostRecentTime = mostRecent[1].lastUpdated;

    for (const entry of entries) {
      if (entry[1].lastUpdated > mostRecentTime) {
        mostRecent = entry;
        mostRecentTime = entry[1].lastUpdated;
      }
    }

    return {
      slug: mostRecent[0],
      name: mostRecent[1].name,
      branding: mostRecent[1].branding,
    };
  } catch (_error) {
    return null;
  }
};

export const clearTenantBrandingCache = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {}
};

const getCachedBrandings = (): Record<
  string,
  CachedTenantData & { lastUpdated: number }
> => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (_error) {
    return {};
  }
};
